import { useAppDispatch, useAppSelector } from "../../../store";
import { useCallback, useContext, useEffect, useState } from "react";
import useFetchAndTrace from "../../../fetchWithTrace";
import {
  addBOMUpdates,
  BillableElement,
  BillOfMaterials,
  billOfMaterialsLoaded,
  BoardElementAddition,
  BoardElementToDelete,
  BoardElementUpdate,
  BoardMachinesStates,
  boardNotFound,
  BOMState,
  DEFAULT_PART_PLACEMENT_CHECK,
  DEFAULT_PART_SELECTION_CHECK,
  pageFailure,
  removeBOMUpdates,
  updateBOMState,
} from "../../../../../reducers/uploadedBoard";
import settings from "../../../../../constants/settings";
import { useSendError } from "../../../../observability/logging";
import { useBoardFromContext } from "../board";
import { useSendUpdateEvent } from "./sendUpdates";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import { useDebouncedCallback } from "use-debounce";
import { BOMUpdate } from "../../../../../reducers/models/boardUpdates";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../state-machine/sendBoardEvent";
import { elementToKey } from "../billOfMaterials";

function useSendBOMUpdatesAndRemoveFromStore() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const { refreshBillOfMaterials } = useRefreshBillOfMaterialsInStore();
  const { sendUpdateEvent } = useSendUpdateEvent(refreshBillOfMaterials);

  return useCallback(
    (bomUpdates: BOMUpdate[]) => {
      if (bomUpdates.length > 0 && boardId) {
        dispatch(removeBOMUpdates({ boardId, bomUpdates }));
        void sendUpdateEvent({
          machine_group: MachineGroup.BOARD,
          model_id: boardId,
          event: BoardTransitionEvent.SHOULD_UPDATE_BOM,
          update: {
            bom_updates: bomUpdates,
          },
        });
      }
    },
    [dispatch, sendUpdateEvent, boardId],
  );
}

export function useSendBOMEvents() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const sendBOMUpdatesAndRemoveFromStore =
    useSendBOMUpdatesAndRemoveFromStore();
  const bomUpdates = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.bomUpdates || null,
  );

  const debouncedUpdateBOM = useDebouncedCallback(
    (updates: BOMUpdate[]) => {
      sendBOMUpdatesAndRemoveFromStore(updates);
    },
    1000,
    { leading: true },
  );

  useEffect(() => {
    if (bomUpdates && bomUpdates.length > 0) {
      debouncedUpdateBOM(bomUpdates);
    }
  }, [debouncedUpdateBOM, bomUpdates]);
}

export function useAddBOMEvent() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();

  return useCallback(
    (bomUpdates: BOMUpdate[], onSuccess?: () => void) => {
      if (boardId) {
        dispatch(addBOMUpdates({ boardId, bomUpdates }));
        onSuccess?.();
      }
    },
    [dispatch, boardId],
  );
}

function useAssertGroupKeyImplementation() {
  const sendError = useSendError();

  return useCallback(
    (billOfMaterials: BillOfMaterials, boardId: string) => {
      let frontendKey = "";
      let backendKey = "";
      let elementName = "";
      const outOfSyncKeys = billOfMaterials.elements.some((element) => {
        frontendKey = elementToKey(element, true);
        backendKey = element.group_key;
        elementName = element.name;
        return frontendKey !== backendKey;
      });

      if (outOfSyncKeys) {
        sendError("BOM group keys are out of sync", {
          additionalAttributes: {
            boardId,
            elementName,
            frontendKey,
            backendKey,
          },
        });
      }
    },
    [sendError],
  );
}

export function useRefreshBillOfMaterialsInStore() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const [refreshingBillOfMaterials, setRefreshingBillOfMaterials] =
    useState(false);
  const fetchAndTrace = useFetchAndTrace();
  const dispatch = useAppDispatch();
  const assertGroupKeyImplementation = useAssertGroupKeyImplementation();

  const handleFetchBOM = useCallback(
    async (response: Response) => {
      setRefreshingBillOfMaterials(false);
      if (response.ok) {
        const billOfMaterials = (await response.json()) as BillOfMaterials;
        dispatch(billOfMaterialsLoaded({ boardId, billOfMaterials }));
        assertGroupKeyImplementation(billOfMaterials, boardId);
      } else if (response.status == 404 && boardId) {
        dispatch(boardNotFound({ boardId }));
      } else {
        throw Error("Failed to fetch bill of materials");
      }
    },
    [assertGroupKeyImplementation, boardId, dispatch],
  );

  const handleFetchBOMError = useCallback(() => {
    setRefreshingBillOfMaterials(false);
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);

  const refreshBillOfMaterials = useCallback(() => {
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/bill-of-materials`,
      handleFetchBOM,
      handleFetchBOMError,
    );
  }, [boardId, fetchAndTrace, handleFetchBOM, handleFetchBOMError]);

  const debouncedRefreshBillOfMaterials = useDebouncedCallback(
    () => {
      refreshBillOfMaterials();
    },
    2000,
    { leading: true },
  );

  const handleBillOfMaterialsStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      if (
        states.bom_state != BOMState.PROCESSED &&
        nextStatesData.bom_state == BOMState.PROCESSED
      ) {
        debouncedRefreshBillOfMaterials();
      } else {
        dispatch(
          updateBOMState({ boardId, bomState: nextStatesData.bom_state }),
        );
      }
    },
    [debouncedRefreshBillOfMaterials, dispatch, boardId],
  );

  return {
    refreshBillOfMaterials: debouncedRefreshBillOfMaterials,
    refreshingBillOfMaterials,
    handleBillOfMaterialsStateUpdates,
  };
}

export function useBillOfMaterials() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  return useAppSelector((state) =>
    boardId
      ? state.uploadedBoard.boards[boardId]?.billOfMaterials || null
      : null,
  );
}

export const useUpdateBoardElementSelection = () => {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const sendBOMEvent = useAddBOMEvent();
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const dispatch = useAppDispatch();

  const updateBoardElementsInStore = useCallback(
    (boardElementUpdates: BoardElementUpdate[]) => {
      const newBillOfMaterials = _.cloneDeep(billOfMaterials);
      for (const boardElementUpdate of boardElementUpdates) {
        const elementIndex = newBillOfMaterials.elements.findIndex(
          (element) => element.id == boardElementUpdate.elementId,
        );
        const element = newBillOfMaterials.elements[elementIndex]!;
        element.do_not_populate = boardElementUpdate.doNotPopulate;
        element.part_id = boardElementUpdate.part_id;
        element.is_consignment = boardElementUpdate.isConsignment;
        element.lock_allocation = boardElementUpdate.lockAllocation;
        if (boardElementUpdate.elementPlacement !== null) {
          element.placement = _.cloneDeep(boardElementUpdate.elementPlacement);
        }
        element.part_placement_check = DEFAULT_PART_PLACEMENT_CHECK;
        element.part_selection_check = DEFAULT_PART_SELECTION_CHECK;
      }
      if (boardId) {
        dispatch(
          billOfMaterialsLoaded({
            boardId,
            billOfMaterials: newBillOfMaterials,
          }),
        );
      }
    },
    [billOfMaterials, dispatch, boardId],
  );

  return (
    boardElementUpdates: BoardElementUpdate[],
    onSuccess?: () => void,
  ) => {
    updateBoardElementsInStore(boardElementUpdates);
    sendBOMEvent(
      boardElementUpdates.map((elementUpdate) => {
        return {
          id: uuidv4(),
          element_update: {
            id: elementUpdate.elementId,
            do_not_populate: elementUpdate.doNotPopulate,
            part_id: elementUpdate.part_id,
            is_consignment: elementUpdate.isConsignment,
            lock_allocation: elementUpdate.lockAllocation,
            element_placement:
              elementUpdate.elementPlacement !== undefined
                ? elementUpdate.elementPlacement
                : null,
          },
          element_deletion: null,
          element_addition: null,
          remove_all_elements: false,
        };
      }),
      onSuccess,
    );
  };
};

export const useAddBoardElement = () => {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const sendBOMEvent = useAddBOMEvent();
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const isDemoBoard = useBoardFromContext().isDemoBoard;
  const dispatch = useAppDispatch();

  return (
    boardElementAdditions: BoardElementAddition[],
    onSuccess?: () => void,
  ) => {
    const newElements: BillableElement[] = boardElementAdditions.map(
      (addition) => {
        const element = {
          id: uuidv4(),
          name: addition.name,
          value_text: addition.valueText,
          footprint_name: "",
          footprint_group_key_component: "",
          properties: [],
          do_not_populate: addition.doNotPopulate,
          part_id: addition.part_id,
          placement: addition.elementPlacement,
          part_placement_check: DEFAULT_PART_PLACEMENT_CHECK,
          part_selection_check: DEFAULT_PART_SELECTION_CHECK,
          is_consignment: addition.isConsignment,
          lock_allocation: false,
          part_specification: null,
          group_key: "",
        };
        return {
          ...element,
          group_key: elementToKey(element, true),
        };
      },
    );
    const newBillOfMaterials = {
      ...billOfMaterials,
      elements: [...billOfMaterials.elements, ...newElements],
    };
    if (boardId) {
      dispatch(
        billOfMaterialsLoaded({ boardId, billOfMaterials: newBillOfMaterials }),
      );
    }
    if (!isDemoBoard) {
      sendBOMEvent(
        newElements.map((element) => {
          return {
            id: uuidv4(),
            remove_all_elements: false,
            element_addition: {
              id: element.id,
              name: element.name,
              value_text: element.value_text,
              do_not_populate: element.do_not_populate,
              part_id: element.part_id,
              is_consignment: element.is_consignment,
              lock_allocation: element.lock_allocation,
              element_placement: element.placement,
            },
            element_deletion: null,
            element_update: null,
          };
        }),
        onSuccess,
      );
    }
  };
};

export const useDeleteBoardElement = () => {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const isDemoBoard = useBoardFromContext().isDemoBoard;
  const sendBOMEvent = useAddBOMEvent();
  const dispatch = useAppDispatch();

  const deleteBoardElementsInStore = useCallback(
    (deletions: BoardElementToDelete[]) => {
      const elementIdsToDelete = new Set(
        deletions.map((deletion) => deletion.elementId),
      );
      const elementsToKeep = billOfMaterials.elements.filter(
        (element) => !elementIdsToDelete.has(element.id),
      );
      const newBillOfMaterials = {
        ...billOfMaterials,
        elements: elementsToKeep,
      };
      if (boardId) {
        dispatch(
          billOfMaterialsLoaded({
            boardId,
            billOfMaterials: newBillOfMaterials,
          }),
        );
      }
    },
    [billOfMaterials, dispatch, boardId],
  );

  return (deletions: BoardElementToDelete[], onSuccess?: () => void) => {
    deleteBoardElementsInStore(deletions);
    if (!isDemoBoard) {
      sendBOMEvent(
        deletions.map((deletion) => {
          return {
            id: uuidv4(),
            remove_all_elements: false,
            element_addition: null,
            element_deletion: {
              id: deletion.elementId,
            },
            element_update: null,
          };
        }),
        onSuccess,
      );
    }
  };
};

export const useClearBOM = () => {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const sendBOMEvent = useAddBOMEvent();
  const isDemoBoard = useBoardFromContext().isDemoBoard;
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const dispatch = useAppDispatch();

  return () => {
    if (boardId) {
      dispatch(
        billOfMaterialsLoaded({
          boardId,
          billOfMaterials: { ...billOfMaterials, elements: [] },
        }),
      );
    }

    if (!isDemoBoard) {
      sendBOMEvent([
        {
          id: uuidv4(),
          remove_all_elements: true,
          element_addition: null,
          element_deletion: null,
          element_update: null,
        },
      ]);
    }
  };
};
